import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { EMPTY_STATE_IMAGES } from '@shared/constants';
import { validateEmail } from '@shared/utils';
import { ChevronRightIcon, DeleteIcon } from '@sketchnote/icons';
import { Divider, LoaderSm, LoaderXs, Switch } from '@sketchnote/shared-components';
import { Unlink } from '@styled-icons/foundation';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API } from 'src/api';
import { useSidebarStore } from 'src/components/sidebar/store';
import { UsersResultsShare } from 'src/components/workspaces';
import { SelectOption } from 'src/features/form/components/SelectOption';
import { useFormsStore } from 'src/features/form/store/useFormsStore';
import { FormSettingsPage, FormToTaskMap } from 'src/features/form/types/form';
import shallow from 'zustand/shallow';

import { useFileExploreStore } from '../store';
import { ExploreHeader } from './home';

const FormSettingsHome = ({
  onChangePage,
  formId,
}: {
  onChangePage: (page: FormSettingsPage) => void;
  formId: string;
}) => {
  const onChangeForm = useFormsStore((s) => s.onChangeForm);
  const formData = useFormsStore(
    useCallback(
      (s) => {
        if (formId) {
          return s.forms[formId];
        }
      },
      [formId]
    )
  );
  return (
    <>
      <div className="text-base">
        <p className="mb-6 font-medium">⚙️ Form settings </p>
        <p className="text-gray-600 ml-4 mb-24 text-sm">Customize how you use your form</p>
      </div>

      <div>
        <div
          onClick={() => onChangePage('response')}
          className="flex justify-between items-center py-18 px-4 border-b text-gray-600 cursor-pointer transition-colors duration-200 hover:bg-gray-100"
        >
          <p className="font-medium text-sm">✍🏼 Edit response</p>
          <div>
            <ChevronRightIcon />
          </div>
        </div>
        <div
          onClick={() => onChangePage('timer')}
          className="flex justify-between items-center py-18 px-4 border-b text-gray-600 cursor-pointer transition-colors duration-200 hover:bg-gray-100"
        >
          <p className="font-medium text-sm">⏰ Form timer</p>
          <div>
            <ChevronRightIcon />
          </div>
        </div>
        <div
          onClick={() => onChangePage('form-to-task')}
          className="flex justify-between items-center py-18 px-4 border-b text-gray-600 cursor-pointer transition-colors duration-200 hover:bg-gray-100"
        >
          <p className="font-medium text-sm">📃 Save results as tasks</p>
          <div>
            <ChevronRightIcon />
          </div>
        </div>
        <div className="flex justify-between items-center py-18 px-4 border-b text-gray-600 transition-colors duration-200 hover:bg-gray-100">
          <p className="font-medium text-sm">👾 Add captcha</p>
          <Switch
            disabled={!!formData?.timer}
            checked={!formData?.timer && !!formData?.captchaEnabled}
            onChange={() => onChangeForm('captchaEnabled', !formData?.captchaEnabled, formId)}
          />
        </div>
        <div
          onClick={() => onChangePage('share-results')}
          className="flex justify-between items-center py-18 px-4 border-b text-gray-600 cursor-pointer transition-colors duration-200 hover:bg-gray-100"
        >
          <p className="font-medium text-sm">📖 Share results</p>
          <div>
            <ChevronRightIcon />
          </div>
        </div>
      </div>
    </>
  );
};

const FormSettingsResponse = ({ formId }: { formId: string }) => {
  const onChangeForm = useFormsStore((s) => s.onChangeForm);
  const formData = useFormsStore(
    useCallback(
      (s) => {
        if (formId) {
          return s.forms[formId];
        }
      },
      [formId]
    )
  );

  const isValidUrl = (url?: string) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };
  return (
    <>
      <div className="text-base">
        <p className="mb-6 font-medium">✍🏼 Edit response</p>
        <p className="text-gray-600 ml-4 mb-24 text-sm">
          Customize your form’s response message on submission
        </p>
      </div>

      <div className="flex flex-col mb-16">
        <label className="mb-8 text-sm text-gray-600" htmlFor="btnText">
          Button text
        </label>
        <input
          onChange={(e) =>
            onChangeForm(
              'submission',
              { ...formData?.submission, buttonText: e.target.value },
              formId
            )
          }
          value={formData?.submission?.buttonText}
          placeholder="Submit"
          className="px-12 py-13 bg-gray-100 rounded-md border border-gray-200"
          id="btnText"
        />
      </div>

      <div className="pt-15 pb-4 text-gray-600 text-sm  border-gray-200">
        <div className="flex justify-between items-end mb-16">
          <div>Redirect on completion</div>

          <span className="text-xs  text-blue-400">
            <Switch
              checked={!!formData?.redirect?.isRedirect}
              onChange={() =>
                onChangeForm(
                  'redirect',
                  { ...formData?.redirect, isRedirect: !formData?.redirect?.isRedirect },
                  formId
                )
              }
            />
          </span>
        </div>

        {formData?.redirect?.isRedirect && (
          <div className={`mt-4  ${!formData?.redirect?.isRedirect} ? 'opacity-50' : 'opactiy-0'`}>
            <div className="border border-gray-200 bg-gray-100 rounded-md text-gray-999 min-w-44  flex items-center p-16">
              <input
                className="w-full p-4 outline-none bg-transparent placeholder-gray-500"
                placeholder="https://"
                disabled={!formData?.redirect?.isRedirect}
                value={formData?.redirect?.url}
                onChange={(e) => {
                  onChangeForm(
                    'redirect',
                    { isRedirect: !!formData?.redirect?.isRedirect, url: e.target.value },
                    formId
                  );
                }}
              />
            </div>

            {formData?.redirect?.url && !isValidUrl(formData?.redirect?.url) ? (
              <div className="mt-8 text-xs font-medium text-red-500">
                *URL needs to start with http:// or https://
              </div>
            ) : (
              <></>
            )}
          </div>
        )}
      </div>

      <div className="py-4 text-xss">
        <Divider text="OR" />
      </div>

      <div
        className={`flex flex-col pb-16 border-b-2 ${
          formData?.redirect?.isRedirect ? 'opacity-50' : 'opactiy-0'
        }`}
      >
        <label className="mb-8 text-sm text-gray-600" htmlFor="responseMessage">
          Message
        </label>
        <textarea
          className="px-12 py-13 bg-gray-100 rounded-md border border-gray-200 h-275"
          id="responseMessage"
          disabled={formData?.redirect?.isRedirect}
          placeholder="Thank you for your response"
          onChange={(e) =>
            onChangeForm('submission', { ...formData?.submission, message: e.target.value }, formId)
          }
          value={formData?.submission?.message}
        />
      </div>
    </>
  );
};

const FormSettingsTimer = ({ formId }: { formId: string }) => {
  const onChangeForm = useFormsStore((s) => s.onChangeForm);
  const formData = useFormsStore(
    useCallback(
      (s) => {
        if (formId) {
          return s.forms[formId];
        }
      },
      [formId]
    )
  );

  const handleTimerMinutes = (mins: number) => {
    const hours = Math.floor((formData?.timer || 0) / 60);
    const newTimer = hours * 60 + mins;

    onChangeForm('timer', newTimer, formId);
  };

  const handleTimerHours = (hours: number) => {
    const mins = Math.floor((formData?.timer || 0) % 60);
    const newTimer = hours * 60 + mins;

    onChangeForm('timer', newTimer, formId);
  };
  return (
    <>
      <div className="text-base">
        <div className="flex justify-between items-end mb-16">
          <p className="font-medium">⏰ Form timer </p>
          <span className="text-xs text-blue-400">
            <Switch
              checked={!!formData?.timer}
              onChange={() => {
                onChangeForm('timer', formData?.timer ? 0 : 5, formId);
              }}
            />
          </span>
        </div>
        <p className="text-gray-600 ml-4 mb-24 text-sm">
          Form response will be automatically submitted at the end of the timer.
          <br />
          No fields can be marked as required in timed mode. Captcha cannot be enabled in this form.
        </p>
      </div>

      <div>
        <div className="flex flex-col mb-16">
          <label className="mb-8 text-sm text-gray-600">Time to respond</label>
          <div className="flex items-center gap-12">
            <select
              onChange={(e) => {
                handleTimerHours(parseInt(e.target.value));
              }}
              placeholder="00"
              className={classNames(
                'px-12 py-13 bg-gray-100 rounded-md border border-gray-200',
                !formData?.timer && 'opacity-75 text-gray-600'
              )}
              value={Math.floor((formData?.timer || 0) / 60)}
              disabled={!formData?.timer}
            >
              {Array.from(Array(24).keys()).map((ignore, index) => (
                <option key={index} value={index}>
                  {index < 10 ? `0${index}` : index}
                </option>
              ))}
            </select>
            <div>:</div>
            <select
              onChange={(e) => {
                handleTimerMinutes(parseInt(e.target.value));
              }}
              placeholder="00"
              className={classNames(
                'px-12 py-13 bg-gray-100 rounded-md border border-gray-200',
                !formData?.timer && 'opacity-75 text-gray-600'
              )}
              value={Math.floor((formData?.timer || 0) % 60)}
              disabled={!formData?.timer}
            >
              {Array.from(Array(60).keys()).map((ignore, index) => (
                <option key={index} value={index}>
                  {index < 10 ? `0${index}` : index}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </>
  );
};

const FormSettingsSaveToTask = ({ formId, onCancel }: { formId: string; onCancel: () => void }) => {
  const router = useRouter();
  const fileId = router.query.slug;
  interface SelectOption {
    label: string;
    value: string;
  }

  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<SelectOption | undefined>();
  const [selectedDisplayQuestion, setSelectedDisplayQuestion] = useState<
    SelectOption | undefined
  >();
  const [saving, setSaving] = useState(false);
  const [unlinkingFile, setUnlinkingFile] = useState(false);
  const [open] = useSnackbar();

  const openUnlinkModal = () => setUnlinkModalOpen(true);
  const closeUnlinkModal = () => setUnlinkModalOpen(false);

  const incompleteForm = !fileId || !fileName || !selectedWorkPlace || !selectedDisplayQuestion;

  const { formData, createFormToTask, setFormToTaskData, unlinkFormToTask, updateFormToTask } =
    useFormsStore(
      useCallback(
        (s) => {
          const formData = s.forms[formId];

          return {
            formData,
            createFormToTask: s.createFormToTask,
            setFormToTaskData: s.setFormToTaskData,
            unlinkFormToTask: s.unlinkFormToTask,
            updateFormToTask: s.updateFormToTask,
          };
        },
        [formId]
      )
    );

  const { workspaces } = useSidebarStore(
    (s) => ({
      workspaces: s.workspaces,
    }),
    shallow
  );

  const handleWorkPlaceSelect = (op) => setSelectedWorkPlace(op);
  const handleQuestionSelect = (op) => setSelectedDisplayQuestion(op);
  const onFileNameChange = (op) => setFileName(op);

  const questionOptions =
    formData?.fields
      ?.filter((field) => field.responseType === 'short-text')
      .map((field) => ({ label: field.title || '', value: field._id || '' })) || [];

  const workSpaceOptions = workspaces.map((ws) => ({ label: ws.name, value: ws._id })) || [];
  const fileLinked = !!formData?.formToTaskMap;

  const updateDisabled =
    fileLinked && selectedDisplayQuestion?.value === formData.formToTaskMap?.field;

  const handleSubmit = async () => {
    if (incompleteForm || saving) return;
    if (fileLinked) await handleUpdateFile();
    else await handleCreateFile();
  };

  const handleUpdateFile = async () => {
    const reqData = {
      formId: formId,
      fieldId: selectedDisplayQuestion?.value || '',
    };
    setSaving(true);
    const res = await updateFormToTask(reqData);
    if (res) {
      if (formData?.formToTaskMap) {
        setFormToTaskData(formId, {
          ...formData.formToTaskMap,
          field: selectedDisplayQuestion?.value || '',
        });
      }
      open({
        text: 'File updated!',
        duration: 2000,
        position: 'top-center',
        snackType: 'standard',
      });
    } else
      open({
        text: 'Error updating file',
        duration: 2000,
        position: 'top-center',
        snackType: 'error',
      });
    setSaving(false);
  };

  const handleCreateFile = async () => {
    const reqData = {
      fileId: `${fileId}`,
      taskFileTitle: fileName,
      workspaceId: selectedWorkPlace?.value || '',
      fieldId: selectedDisplayQuestion?.value || '',
    };
    setSaving(true);
    const res = await createFormToTask(reqData);
    if (res) {
      const formToTaskMap: FormToTaskMap = {
        field: res.data.formToTaskMap.field,
        file: {
          workspace: selectedWorkPlace?.value || '',
          _id: res.data.formToTaskMap.file,
          title: res.data.title,
        },
      };
      setFormToTaskData(formId, formToTaskMap);
      open({
        text: 'File created!',
        duration: 2000,
        position: 'top-center',
        snackType: 'standard',
      });
    } else
      open({
        text: 'Error creating file',
        duration: 2000,
        position: 'top-center',
        snackType: 'error',
      });
    setSaving(false);
  };

  const handleUnlink = async () => {
    if (!fileLinked || unlinkingFile) return;
    setUnlinkingFile(true);
    const res = await unlinkFormToTask(formId);
    if (res) {
      setFormToTaskData(formId, undefined);
      open({
        text: 'File unlinked!',
        duration: 2000,
        position: 'top-center',
        snackType: 'standard',
      });
    } else
      open({
        text: 'Error unlinking file',
        duration: 2000,
        position: 'top-center',
        snackType: 'error',
      });
    setUnlinkingFile(true);
    closeUnlinkModal();
  };

  useEffect(() => {
    if (formData?.formToTaskMap) {
      const { formToTaskMap } = formData;
      setFileName(formToTaskMap.file.title);
      const workspace = workSpaceOptions.find((ws) => ws.value === formToTaskMap.file.workspace);
      if (workspace) setSelectedWorkPlace(workspace);
      const field = questionOptions.find((f) => f.value === formToTaskMap.field);
      if (field) setSelectedDisplayQuestion(field);
    }
  }, [formData?.formToTaskMap]);

  return (
    <div className="px-1">
      <div className="text-base">
        <div className="flex justify-between items-end mb-16">
          <p className="font-medium mb-6">📃 Save results as tasks</p>
        </div>
        <p className="text-gray-600 ml-4 mb-24 text-sm">
          Create a file where you can store your form results as tasks
        </p>
      </div>

      <div>
        <div className="flex flex-col mb-16">
          <label className="mb-8 text-sm text-gray-600" htmlFor="btnText">
            File Name
          </label>
          <span
            className={classNames(
              'flex gap-12 px-12 py-13 bg-gray-100 rounded-md border border-gray-200'
            )}
          >
            <input
              disabled={fileLinked}
              className="w-full bg-gray-100 flex-shrink-1"
              value={fileName}
              onChange={(e) => onFileNameChange(e.target.value)}
              placeholder="What do you want the file to be called?"
              id="btnText"
            />
            {fileLinked && (
              <button
                onClick={openUnlinkModal}
                className="flex gap-8 flex-shrink-0 items-center text-blue-600 text-sm"
              >
                <Unlink size={14} /> Unlink file
              </button>
            )}
            <Modal isCentered isOpen={unlinkModalOpen} onClose={closeUnlinkModal}>
              <ModalOverlay />
              <ModalContent style={{ maxWidth: '460px', borderRadius: '8px' }}>
                <ModalCloseButton style={{ top: 30, right: 32 }} />
                <div className="p-32 flex flex-col">
                  <div className="mb-6">
                    <div className="flex">
                      <span className="text-lg text-black font-medium block ">Are you sure?</span>
                    </div>
                    <div className="text-gray-600 text-sm mt-24">
                      If you unlink this file, you won&apos;t be able to add new responses as tasks
                      to the file. To save responses as tasks again, you&apos;ll need to link a new
                      file altogether.
                    </div>
                    <div className="flex justify-end align-center gap-12 mt-24">
                      <button
                        onClick={closeUnlinkModal}
                        className="my-auto py-12 px-24 flex justify-around rounded-sm font-medium items-center border border-blue-400 text-blue-400"
                      >
                        Cancel
                      </button>
                      <button
                        className={classNames(
                          'my-auto py-12 px-24 flex justify-around rounded-sm font-medium bg-blue-500 text-white items-center gap-12'
                        )}
                        onClick={handleUnlink}
                        disabled={unlinkingFile}
                      >
                        Yes, Unlink file
                        {unlinkingFile && (
                          <LoaderXs style={{ fontSize: 5, width: 16, height: 16 }} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </ModalContent>
            </Modal>
          </span>
        </div>
        <div className="flex flex-col mb-16">
          <label className="mb-8 text-sm text-gray-600" htmlFor="btnText">
            Workspace name
          </label>
          <SelectOption
            disabled={fileLinked}
            value={selectedWorkPlace}
            options={workSpaceOptions}
            onChange={handleWorkPlaceSelect}
            placeholder={'Select a file location'}
          />
        </div>
        <div className="flex flex-col mb-16">
          <label className="mb-8 text-sm text-gray-600" htmlFor="btnText">
            Task Name
          </label>
          <SelectOption
            value={selectedDisplayQuestion}
            options={questionOptions}
            onChange={handleQuestionSelect}
            placeholder={'Select a display question'}
          />
        </div>
        <div className="flex justify-end gap-12 items-center">
          <button
            onClick={onCancel}
            className="my-auto px-24 py-12 rounded-sm font-medium text-blue-500 border border-blue-500 hover:bg-blue-100"
          >
            Cancel
          </button>

          <span>
            <button
              onClick={handleSubmit}
              disabled={updateDisabled}
              className={classNames(
                'my-auto px-24 py-12 rounded-sm font-medium bg-blue-500 text-white flex items-center gap-12',
                updateDisabled && 'opacity-75'
              )}
            >
              {saving && <LoaderXs style={{ fontSize: 5, width: 16, height: 16 }} />}
              {saving
                ? fileLinked
                  ? 'Updating'
                  : 'Linking File'
                : fileLinked
                ? 'Update'
                : 'Link File'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};
const FormSettingsShareResults = ({
  formId,
}: {
  formId: string;
  onCancel: () => void;
  members?: {
    firstname: string;
    memberId: string;
    type: string;
    avatar?: string;
    email: string;
  }[];
}) => {
  const [sharedEmailsList, setSharedEmailsList] = useState<string[]>([]);
  const [loadingEmails, setLoadingEmails] = useState<boolean>(true);
  const [addingEmail, setAddingEmail] = useState<boolean>(false);
  const [confirmAddEmail, setConfirmAddEmail] = useState<boolean>(false);
  const [emailInput, setEmailInput] = useState<string>('');
  const [open] = useSnackbar();
  const isEmailValid = useMemo(
    () => !loadingEmails && validateEmail(emailInput) && !sharedEmailsList.includes(emailInput),
    [emailInput, sharedEmailsList, loadingEmails]
  );

  const handleAddEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEmailValid || addingEmail) return;
    addEmail();
  };

  const getSharedEmailsList = async () => {
    setLoadingEmails(true);
    try {
      const res = await API.get(`/forms/email?id=${formId}&page=1&limit=999`);
      const emails = res.data.data.docs;
      setSharedEmailsList([...emails]);
    } catch (e) {
      open({
        text: 'Error loading shared emails!',
        duration: 2000,
        position: 'top-center',
        snackType: 'error',
      });
    }
    setLoadingEmails(false);
  };

  const cancelAddEmail = () => setConfirmAddEmail(false);

  const addEmail = async () => {
    setConfirmAddEmail(false);
    if (!isEmailValid || addingEmail) return;
    setAddingEmail(true);
    try {
      const res = await API.post(`/forms/email`, { id: formId, email: emailInput });
      setSharedEmailsList([...sharedEmailsList, res.data.data.email]);
    } catch (e) {
      open({
        text: 'Error adding email!',
        duration: 2000,
        position: 'top-center',
        snackType: 'error',
      });
    }
    setEmailInput('');
    setAddingEmail(false);
  };

  useEffect(() => {
    getSharedEmailsList();
  }, []);

  const handleDeleteEmail = (email: string) => {
    setSharedEmailsList((p) => p.filter((e) => e !== email));
  };

  return (
    <>
      <div className="text-base">
        <div className="flex justify-between items-end mb-16">
          <p className="font-medium mb-6">📖 Share results</p>
        </div>
        <p className="text-gray-600 ml-4 mb-24 text-sm">
          Share form results with anyone whether they use sketchnote or not
        </p>
      </div>

      <div className="border-b">
        <div className="flex items-center gap-12 mb-12 text-sm text-gray-600">
          <div>To:</div>
          <form className="flex items-center flex-grow gap-8" onSubmit={handleAddEmail}>
            <input
              className="w-full py-5 flex-shrink-1 flex-grow"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Add an email"
              id="btnText"
              autoFocus
            />
            {isEmailValid && (
              <button
                className={classNames(
                  'my-auto flex items-center gap-4 px-12 py-4 rounded-sm font-medium text-blue-500 border border-blue-500 hover:bg-blue-100',
                  addingEmail && 'opacity-75'
                )}
                type="submit"
                disabled={addingEmail}
              >
                {addingEmail ? (
                  <>
                    <LoaderXs style={{ fontSize: 5, width: 16, height: 16 }} />
                    Inviting
                  </>
                ) : (
                  <>Invite</>
                )}
              </button>
            )}
          </form>
        </div>
      </div>
      <div className="mt-32 text-sm">
        <div className="text-gray-600">Shared with</div>
        <div className="mt-12">
          {sharedEmailsList.length ? (
            <>
              {sharedEmailsList.map((email, index) => (
                <UserRow key={index} email={email} formId={formId} onDelete={handleDeleteEmail} />
              ))}
            </>
          ) : (
            <div className="flex flex-col items center gap-12 text-center">
              <img src={EMPTY_STATE_IMAGES.creationsDeleted} />
              <div>
                You can share view access of results with anyone whether they use sketchnote or not
              </div>
              {loadingEmails && (
                <img
                  className="mt-12"
                  src="https://res.cloudinary.com/samvibes/image/upload/v1683787135/Forms/forms-loader.gif"
                />
              )}
            </div>
          )}
        </div>
        <Modal isCentered isOpen={confirmAddEmail} onClose={cancelAddEmail}>
          <ModalOverlay />
          <ModalContent style={{ maxWidth: '460px', borderRadius: '8px' }}>
            <ModalCloseButton style={{ top: 30, right: 32 }} />
            <div className="p-32 flex flex-col">
              <div className="mb-6">
                <div className="flex">
                  <span className="text-lg text-black font-medium block ">Disclamer</span>
                </div>
                <div className="text-gray-600 text-sm mt-24">
                  You’re sharing with someone outside of your organization. They will be notified
                  via email.
                </div>
                <div className="flex justify-end align-center gap-12 mt-24">
                  <button
                    onClick={cancelAddEmail}
                    className="my-auto py-12 px-24 flex justify-around rounded-sm font-medium items-center border border-blue-400 text-blue-400"
                  >
                    Cancel
                  </button>
                  <button
                    className={classNames(
                      'my-auto py-12 px-24 flex justify-around rounded-sm font-medium bg-blue-500 text-white items-center gap-12'
                    )}
                    onClick={addEmail}
                  >
                    Yes, Share
                  </button>
                </div>
              </div>
            </div>
          </ModalContent>
        </Modal>
      </div>
    </>
  );
};

const UserRow = ({
  email,
  formId,
  onDelete,
}: {
  email: string;
  formId: string;
  onDelete: (email: string) => void;
}) => {
  const [open] = useSnackbar();
  const [deletingEmail, setDeletingEmail] = useState<boolean>(false);
  const handleDelete = async (email: string) => {
    setDeletingEmail(true);
    try {
      const res = await API.delete(`/forms/email?id=${formId}&email=${email}`);
      if (res.data?.success === false) throw 'API failure';
      onDelete(email);
    } catch (e) {
      open({
        text: 'Error deleting email!',
        duration: 2000,
        position: 'top-center',
        snackType: 'error',
      });
    }
    setDeletingEmail(false);
  };

  return (
    <div className="border-b border-gray-200">
      <div className="flex items-center">
        <UsersResultsShare className="flex-grow" email={email} avatar="" id="" username="" />
        {deletingEmail ? (
          <LoaderXs style={{ fontSize: 5, width: 16, height: 16 }} />
        ) : (
          <DeleteIcon className="cursor-pointer" onClick={() => handleDelete(email)} />
        )}
      </div>
    </div>
  );
};

export const FormSettings = () => {
  const router = useRouter();

  const formId = router.query.view as string;

  const [page, setPage] = useState<FormSettingsPage>('home');
  const formData = useFormsStore(
    useCallback(
      (s) => {
        if (formId) {
          return s.forms[formId];
        }
      },
      [formId]
    )
  );

  const onChangeForm = useFormsStore((s) => s.onChangeForm);

  function handleToggleSketchnoteBranding() {
    onChangeForm('showSketchnoteBranding', !formData?.showSketchnoteBranding, formId);
  }
  const { closeExploreMenu, setNavigation } = useFileExploreStore();

  const hanldeCloseMenu = () => {
    closeExploreMenu();
  };
  const handleOnBack = () => {
    if (page === 'home') setNavigation('home');
    else setPage('home');
  };

  const handlePageSelect = (page: FormSettingsPage) => setPage(page);

  const getPageComponent = () => {
    switch (page) {
      default:
      case 'home':
        return <FormSettingsHome onChangePage={handlePageSelect} formId={formId} />;
      case 'response':
        return <FormSettingsResponse formId={formId} />;
      case 'timer':
        return <FormSettingsTimer formId={formId} />;
      case 'form-to-task':
        return <FormSettingsSaveToTask onCancel={handleOnBack} formId={formId} />;
      case 'share-results':
        return <FormSettingsShareResults onCancel={handleOnBack} formId={formId} />;
    }
  };

  return (
    <div>
      <div className="h-60 bg-white sticky top-0 z-10">
        <ExploreHeader title="" onBackClick={handleOnBack} onCloseClick={hanldeCloseMenu} />
      </div>
      <div className="pb-10">{getPageComponent()}</div>
    </div>
  );
};
